import React, { useState, useEffect, useRef, useCallback } from "react";
import api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import { cn } from "../../lib/utils";
import { Heart, Reply, Trash2, Send, MessageSquare, Users } from "lucide-react";
import { Avatar } from "./BoardCells";
import { Spinner } from "../ui";
import { timeAgo, personOf, renderWithMentions } from "./social";

function Composer({ members, onSubmit, placeholder, autoFocus, testidPrefix = "update", compact }) {
  const { t } = useApp();
  const [text, setText] = useState("");
  const [mentions, setMentions] = useState([]);
  const [menu, setMenu] = useState(null);
  const [busy, setBusy] = useState(false);
  const taRef = useRef(null);

  const onChange = (e) => {
    const v = e.target.value;
    setText(v);
    const upto = v.slice(0, e.target.selectionStart ?? v.length);
    const m = upto.match(/@([^\s@]*)$/);
    setMenu(m ? { query: m[1].toLowerCase() } : null);
  };
  const tokenFor = (id) => (id === "everyone" ? "everyone" : members.find((x) => x.user_id === id)?.name);
  const pick = (opt) => {
    setText((v) => v.replace(/@([^\s@]*)$/, `@${opt.token} `));
    setMentions((s) => (s.includes(opt.user_id) ? s : [...s, opt.user_id]));
    setMenu(null);
    requestAnimationFrame(() => taRef.current?.focus());
  };
  const submit = async () => {
    const v = text.trim();
    if (!v || busy) return;
    const finalMentions = mentions.filter((id) => { const tk = tokenFor(id); return tk && v.includes(`@${tk}`); });
    setBusy(true);
    try {
      await onSubmit(v, finalMentions);
      setText(""); setMentions([]); setMenu(null);
    } finally { setBusy(false); }
  };
  const q = menu ? menu.query : "";
  const everyoneMatch = menu && (q === "" || "everyone".startsWith(q) || "all".startsWith(q) || "tous".startsWith(q));
  const suggestions = menu ? [
    ...(everyoneMatch ? [{ user_id: "everyone", name: t("mention_everyone"), token: "everyone", everyone: true }] : []),
    ...members.filter((m) => (m.name || "").toLowerCase().includes(q)).slice(0, 6).map((m) => ({ ...m, token: m.name })),
  ] : [];

  return (
    <div className="relative">
      <div className={cn("rounded-xl border bg-surface focus-within:border-primary/60 transition-colors", compact ? "p-2" : "p-2.5")}>
        <textarea ref={taRef} value={text} onChange={onChange} autoFocus={autoFocus} rows={compact ? 1 : 2}
          data-testid={`${testidPrefix}-composer-input`} placeholder={placeholder || t("updates_placeholder")}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); } }}
          className="w-full bg-transparent text-sm outline-none resize-none text-text-primary placeholder:text-text-secondary" />
        <div className="flex items-center justify-between mt-1.5">
          <span className="text-[10px] text-text-secondary">{t("updates_mention_hint")}</span>
          <button onClick={submit} disabled={!text.trim() || busy} data-testid={`${testidPrefix}-submit`}
            className={cn("inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors",
              text.trim() && !busy ? "bg-primary text-white hover:opacity-90" : "bg-surface-hover text-text-secondary cursor-not-allowed")}>
            {busy ? <Spinner className="w-3.5 h-3.5" /> : <Send className="w-3.5 h-3.5" />}{t("updates_post")}
          </button>
        </div>
      </div>
      {suggestions.length > 0 && (
        <div className="absolute left-0 right-0 bottom-full mb-1 z-10 rounded-xl border bg-background shadow-xl overflow-hidden" data-testid={`${testidPrefix}-mention-menu`}>
          {suggestions.map((m) => (
            <button key={m.user_id} onClick={() => pick(m)} data-testid={`mention-opt-${m.user_id}`}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-surface-hover text-left transition-colors">
              {m.everyone
                ? <span className="w-6 h-6 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0"><Users className="w-3.5 h-3.5" /></span>
                : <Avatar person={m} size={24} />}
              <span className="text-sm text-text-primary truncate">{m.everyone ? `@${m.name}` : m.name}</span>
              <span className="text-[10px] text-text-secondary ml-auto">{m.everyone ? t("mention_everyone_hint") : m.role}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function UpdateCard({ u, members, me, onLike, onDelete, onReply, isReply }) {
  const { lang } = useApp();
  const mine = u.actor_id === me;
  return (
    <div className={cn("group/up", isReply && "pl-3 border-l-2 border-surface-hover ml-2")} data-testid={`update-${u.id}`}>
      <div className="flex items-start gap-2.5">
        <Avatar person={personOf(u)} size={isReply ? 24 : 30} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-text-primary truncate">{u.actor_name}</span>
            <span className="text-[11px] text-text-secondary">{timeAgo(u.created_at, lang)}</span>
            {mine && (
              <button onClick={() => onDelete(u)} data-testid={`update-delete-${u.id}`}
                className="ml-auto opacity-0 group-hover/up:opacity-100 text-text-secondary hover:text-red-500 transition-all"><Trash2 className="w-3.5 h-3.5" /></button>
            )}
          </div>
          <p className="text-sm text-text-primary mt-0.5 whitespace-pre-wrap break-words">{renderWithMentions(u.text, members)}</p>
          <div className="flex items-center gap-3 mt-1.5">
            <button onClick={() => onLike(u)} data-testid={`update-like-${u.id}`}
              className={cn("inline-flex items-center gap-1 text-[11px] font-medium transition-colors", u.liked ? "text-red-500" : "text-text-secondary hover:text-text-primary")}>
              <Heart className={cn("w-3.5 h-3.5", u.liked && "fill-current")} />{u.like_count > 0 ? u.like_count : ""}
            </button>
            {!isReply && (
              <button onClick={() => onReply(u)} data-testid={`update-reply-${u.id}`}
                className="inline-flex items-center gap-1 text-[11px] font-medium text-text-secondary hover:text-text-primary transition-colors">
                <Reply className="w-3.5 h-3.5" />{u.replies?.length ? u.replies.length : ""}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function UpdatesTab({ itemId, members, me }) {
  const { t, toast } = useApp();
  const [updates, setUpdates] = useState(null);
  const [replyTo, setReplyTo] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/board-items/${itemId}/updates`);
    setUpdates(data);
  }, [itemId]);
  useEffect(() => { setUpdates(null); setReplyTo(null); load(); }, [load]);

  const post = async (text, mentions, parent_id) => {
    await api.post(`/board-items/${itemId}/updates`, { text, mentions, parent_id });
    setReplyTo(null);
    await load();
  };
  const like = async (u) => {
    const { data } = await api.post(`/board-updates/${u.id}/like`);
    setUpdates((list) => list.map((x) => {
      if (x.id === u.id) return { ...x, liked: data.liked, like_count: data.like_count };
      return { ...x, replies: (x.replies || []).map((r) => (r.id === u.id ? { ...r, liked: data.liked, like_count: data.like_count } : r)) };
    }));
  };
  const del = async (u) => {
    try { await api.delete(`/board-updates/${u.id}`); await load(); toast(t("updates_deleted")); }
    catch { toast(t("updates_error"), { variant: "error" }); }
  };

  return (
    <div className="space-y-4" data-testid="updates-tab">
      <Composer members={members} onSubmit={(text, m) => post(text, m, null)} />
      {updates === null ? (
        <div className="py-8 flex justify-center"><Spinner className="w-6 h-6 text-text-secondary" /></div>
      ) : updates.length === 0 ? (
        <div className="py-10 text-center text-sm text-text-secondary">
          <MessageSquare className="w-7 h-7 mx-auto mb-2 opacity-40" />{t("updates_empty")}
        </div>
      ) : (
        <div className="space-y-4">
          {updates.map((u) => (
            <div key={u.id} className="space-y-2.5">
              <UpdateCard u={u} members={members} me={me} onLike={like} onDelete={del} onReply={(x) => setReplyTo(replyTo === x.id ? null : x.id)} />
              {(u.replies || []).map((r) => (
                <UpdateCard key={r.id} u={r} members={members} me={me} onLike={like} onDelete={del} onReply={() => {}} isReply />
              ))}
              {replyTo === u.id && (
                <div className="pl-8">
                  <Composer members={members} autoFocus compact testidPrefix={`reply-${u.id}`}
                    placeholder={t("updates_reply_placeholder")} onSubmit={(text, m) => post(text, m, u.id)} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
